import React, { useEffect, useMemo, useState, useRef } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { useDataBus } from "@/lib/dataBus";
import {
  LayoutDashboard, Users, FolderOpen, LogOut, ChevronRight, ChevronDown,
  Sparkles, Megaphone, Search, LayoutGrid, UserCog, UserCheck, Pencil, Plus,
  Trash2, Share2, Check as CheckIcon, ClipboardList, CalendarCheck, Wallet,
  ListChecks, Table, User as UserIcon, TrendingUp, KeyRound, MoreHorizontal,
  UserPlus, UserMinus, ArrowRightLeft, ExternalLink, Sun, Moon, PanelLeftClose, PanelLeftOpen
} from "lucide-react";
import useTheme from "@/hooks/useTheme";
import {
  CreateMarketingTeamDialog,
  RenameMarketingTeamDialog,
  ManageMarketingMembersDialog,
  DeleteMarketingTeamDialog,
} from "@/components/marketing/MarketingTeamDialogs";
import ProductivityMarkDialog from "@/components/ProductivityMarkDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RoleTag, WorkflowTag } from "@/components/MultiUserSelect";
import EditProjectDialog from "@/components/EditProjectDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import DaySplash from "@/components/DaySplash";
import ChangePasswordDialog from "@/layouts/dialogs/ChangePasswordDialog";
import QuickCreateDialog from "@/layouts/dialogs/QuickCreateDialog";
import ShareDialog from "@/layouts/dialogs/ShareDialog";
import AddNodeDialog from "@/layouts/dialogs/AddNodeDialog";
import ManageTeamDialog from "@/layouts/dialogs/ManageTeamDialog";
import { TeamLeadsPopover } from "@/layouts/sidebar/SidebarNodes";
import { RowMenu, TreeRow, ProjectRow, SidebarListRow, QuickCreateButton } from "@/layouts/sidebar/SidebarRows";

const DEPARTMENTS = [
  { key: "content",   label: "Content Team",   icon: LayoutGrid, active: true },
  { key: "seo",       label: "SEO Team",       icon: Search,     active: true },
  { key: "marketing", label: "Marketing Team", icon: Megaphone,  active: true },
  { key: "hr",        label: "HR Team",        icon: UserCog,    active: true, kind: "hr" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { isDark, toggle: toggleTheme } = useTheme();
  const nav = useNavigate();
  const loc = useLocation();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [autoExpanded, setAutoExpanded] = useState(false);

  // Rename state: {kind: 'cm'|'tl'|'project', id, value}
  const [renaming, setRenaming] = useState(null);

  // Add-node dialog
  const [addDialog, setAddDialog] = useState(null); // {kind: 'cm'|'tl'|'project', parent}

  // Right-click context menu
  const [contextMenu, setContextMenu] = useState(null); // {x, y, kind, id, name}

  // Change-password dialog visibility (self-service password reset).
  const [pwOpen, setPwOpen] = useState(false);
  // Iter 42.5.3 — global "Mark My Productivity" menu (moved from the
  // per-project spreadsheet header per user request). The dialog itself
  // hosts a project picker for standalone use.
  const [prodMarkOpen, setProdMarkOpen] = useState(false);
  // Only actual producers (associates + team_leads) see the button.
  const canMarkProductivity = user?.role === "associate" || user?.role === "team_lead";
  // Share dialog
  const [shareDialog, setShareDialog] = useState(null); // {kind, id, name}
  const [manageTeam, setManageTeam] = useState(null); // {team, cm}
  // Shares granted to me (for "Shared with you" section)
  const [mySharesData, setMySharesData] = useState([]);
  const [seoFolders, setSeoFolders] = useState([]);
  // Marketing Teams list — rendered under the Marketing dept node.
  const [marketingTeams, setMarketingTeams] = useState([]);
  // Marketing team dialogs
  const [createMktTeam, setCreateMktTeam] = useState(false);
  const [renameMktTeam, setRenameMktTeam] = useState(null);
  const [membersMktTeam, setMembersMktTeam] = useState(null);
  const [deleteMktTeam, setDeleteMktTeam] = useState(null);
  // Sidebar Task Lists / Custom Tables — grouped by "{parent_kind}:{parent_id}" key.
  const [sidebarLists, setSidebarLists] = useState([]);
  // Quick-Create modal state: { parent_kind, parent_id, kind, parent_name }
  const [quickCreate, setQuickCreate] = useState(null);

  const reload = async () => {
    try {
      const [p, u, s, sf, sl, t, mt] = await Promise.all([
        api.get("/projects"),
        api.get("/users"),
        api.get("/shares/me").catch(() => ({ data: [] })),
        api.get("/seo/folders").catch(() => ({ data: [] })),
        api.get("/sidebar-lists").catch(() => ({ data: [] })),
        api.get("/teams").catch(() => ({ data: [] })),
        api.get("/marketing-teams").catch(() => ({ data: [] })),
      ]);
      setProjects(p.data);
      setUsers(u.data);
      setMySharesData(s.data || []);
      setSeoFolders(sf.data || []);
      setSidebarLists(sl.data || []);
      setTeams(t.data || []);
      setMarketingTeams(mt.data || []);
    } catch {}
  };

  useEffect(() => { reload(); }, [loc.pathname]);
  // Iter 36.7 Phase 3 — instant sidebar refresh on any Team-tab / Projects-tab
  // / Manage-Team mutation. No polling, no manual reload needed.
  useDataBus(reload, ["users", "projects", "sidebar", "any"]);
  // External signal (e.g. list page firing sidebar-lists-changed after delete).
  useEffect(() => {
    const h = () => reload();
    window.addEventListener("sidebar-lists-changed", h);
    return () => window.removeEventListener("sidebar-lists-changed", h);
  }, []);

  // Group lists by parent for O(1) lookup during tree render.
  const listsByParent = useMemo(() => {
    const map = {};
    for (const l of sidebarLists) {
      const k = `${l.parent_kind}:${l.parent_id}`;
      (map[k] = map[k] || []).push(l);
    }
    return map;
  }, [sidebarLists]);

  // Close context menu on any global click / escape
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("keydown", (e) => e.key === "Escape" && close());
    return () => window.removeEventListener("click", close);
  }, [contextMenu]);

  const openContext = (e, kind, id, name, extra = {}) => {
    if (user?.role === "associate") return;
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, kind, id, name, ...extra });
  };

  const doDelete = async (kind, id, name, extra = {}) => {
    setContextMenu(null);
    // Copy for confirm messages — deliberately explicit so admins understand
    // sidebar removal DOES NOT delete a user's account (that only happens
    // from the Team tab). Sidebar removal only strips the role/team fields.
    const messages = {
      cm: `Remove ${name} as Content Manager?\n\nTheir account STAYS in your team — they become an unassigned member you can reassign. Any teams under them will also become unassigned.`,
      tl: `Remove ${name} as Team Lead?\n\nTheir account STAYS in your team — they become an unassigned member you can reassign. Associates under them keep their accounts too.`,
      team: `Remove team "${name}"?\n\nBoth Team Leads keep their accounts and become unassigned. No one is deleted.`,
      project: `Delete project "${name}"? This cannot be undone.`,
      sidebar_list: `Delete list "${name}"? This cannot be undone.`,
      seo_folder: `Delete folder "${name}"? This cannot be undone.`,
    };
    if (!confirm(messages[kind] || `Delete ${name}? This cannot be undone.`)) return;
    // Payload used to detach a user from the hierarchy while preserving
    // the account. `role: "associate"` = plain member; empty strings clear
    // the reporting chain fields (matches how EditMemberDialog resets them).
    const detachPayload = {
      role: "associate",
      position: "Writer",
      manager_id: "",
      team_lead_id: "",
      team_name: "",
      tl_kind: "",
    };
    try {
      if (kind === "cm" || kind === "tl") {
        await api.patch(`/users/${id}`, detachPayload);
        toast.success("Removed from hierarchy");
      } else if (kind === "team") {
        // Iter 36.7 Phase 2.6 — teams are now persisted rows. Removing a team
        // means deleting the row AND detaching any TLs still tagged with it.
        // Projects stay in the DB (they become dept-level "Unassigned").
        try {
          if (extra.team_row_id) {
            // Persisted team → backend cascade handles TL detach.
            await api.delete(`/teams/${extra.team_row_id}`);
          } else {
            // Legacy derived team without a row → fall back to the old
            // detach-TLs path.
            const tlIds = (extra.tls || []).filter(Boolean);
            if (tlIds.length === 0) { toast.error("No team leads found to remove"); return; }
            await Promise.all(tlIds.map((tid) => api.patch(`/users/${tid}`, { team_name: "", tl_kind: "" })));
          }
          toast.success("Team removed");
        } catch (e) { throw e; }
      } else if (kind === "project") {
        await api.delete(`/projects/${id}`);
        toast.success("Deleted");
      } else if (kind === "sidebar_list") {
        await api.delete(`/sidebar-lists/${id}`);
        toast.success("Deleted");
      } else if (kind === "seo_folder") {
        await api.delete(`/seo/folders/${id}`);
        toast.success("Deleted");
      }
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Anyone above associate can invoke the quick-create menu (per user directive).
  const canQuickCreate = user && ["super_admin", "admin", "department_admin", "content_manager", "team_lead"].includes(user.role);
  // Opens the "name this list" modal for the given hierarchy target.
  const openQuickCreate = (parent_kind, parent_id, parent_name) => (kind) => {
    setQuickCreate({ parent_kind, parent_id, parent_name, kind });
  };

  const tree = useMemo(() => {
    const managers = users.filter((u) => u.role === "content_manager");
    const tls = users.filter((u) => u.role === "team_lead");

    // Iter 36.8 — Strict Department RBAC. Departments a user is NOT associated
    // with (via positions/departments or a visible project) must be hidden
    // entirely from the sidebar. Admin-tier bypasses this rule.
    const isAdminTier = user && ["super_admin", "admin"].includes(user.role);
    const userDeptSet = new Set();
    if (user) {
      (user.departments || []).forEach((d) => d && userDeptSet.add(d));
      if (user.department) userDeptSet.add(user.department);
      (user.positions || []).forEach((p) => p?.department && userDeptSet.add(p.department));
    }
    // Any project visible to me → include its department. Covers TLs / associates
    // whose only anchor is a project assignment or a share.
    projects.forEach((p) => { if (p.department) userDeptSet.add(p.department); });

    const visibleDepts = DEPARTMENTS.filter((dept) => {
      if (isAdminTier) return true;
      // HR dept has its own inner gate (canSeeHR) — leave visible here and let
      // the render layer decide, otherwise we'd hide it for admins with dept="hr"
      // vs non-HR staff. The render layer already handles this correctly.
      if (dept.kind === "hr") return true;
      return userDeptSet.has(dept.key);
    });

    return visibleDepts.map((dept) => {
      const deptProjects = projects.filter((p) => (p.department || "content") === dept.key);
      const deptManagers = managers
        .filter((m) => (m.department || "content") === dept.key)
        .map((m) => {
          // Iter 36.7 Phase 2.8 — flattened hierarchy per user directive:
          // Content Team > Content Managers > Team Leads > Projects. No team
          // grouping layer. TLs sit directly under their CM. Each TL keeps
          // their own projects (via team_lead_ids).
          //
          // Iter 36.7 Phase 2.9 — hide Publishing TLs from the sidebar tree
          // (they still appear on the People page). The hierarchy shows
          // only Content-kind TLs: strict "CM > Content TLs > Projects".
          const teamLeads = tls
            .filter((tl) => tl.manager_id === m.id && tl.tl_kind !== "publishing")
            .map((tl) => ({
              ...tl,
              projects: deptProjects.filter((p) => (p.team_lead_ids || []).includes(tl.id)),
            }));

          // Iter 36.7 Phase 2.11 — if the CM has self-picked as TL on any
          // project (and no actual TL is on it too), show them as a
          // synthetic TL node UNDER their own CM row. Strict hierarchy:
          //   Content Team → Komal (CM) → Komal (TL) → projects.
          // We reuse the CM's real user id so Share/… menu items resolve
          // to the correct user; a `_selfPickedFromCM` flag hides Rename
          // and Remove (those would be nonsensical on the pseudo node).
          const cmSelfTLProjects = deptProjects.filter((p) => {
            if (!(p.team_lead_ids || []).includes(m.id)) return false;
            const hasRealTL = (p.team_lead_ids || []).some((tid) => teamLeads.some((tl) => tl.id === tid));
            return !hasRealTL;
          });
          if (cmSelfTLProjects.length > 0) {
            teamLeads.push({
              ...m,
              projects: cmSelfTLProjects,
              _selfPickedFromCM: true,
            });
          }
          return { ...m, teamLeads };
        });

      // No "orphan TLs" section anymore. TLs without a CM are simply not shown
      // in the sidebar — they still appear on the People page.
      return { ...dept, managers: deptManagers };
    });
  }, [users, projects, user]);

  useEffect(() => {
    if (!autoExpanded && users.length > 0) {
      const next = { "dept-content": true };
      const content = tree.find((d) => d.key === "content");
      (content?.managers || []).forEach((cm) => {
        next[`cm-${cm.id}`] = true;
        (cm.teams || []).forEach((t) => { next[`team-${t.id}`] = true; });
      });
      setExpanded((prev) => ({ ...prev, ...next }));
      setAutoExpanded(true);
    }
    // eslint-disable-next-line
  }, [users, tree]);

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const canEdit = user?.role === "super_admin" || user?.role === "admin" || user?.role === "content_manager" || user?.role === "department_admin";
  // Iter 36.9.13 — Bug 17. Rank-aware modify guard, mirrors the backend
  // `_rank()` check on PATCH /users/:id. A caller can only remove / rename
  // a hierarchy node whose rank is STRICTLY LOWER than the caller's.
  // Admin-tier bypasses. Multi-role users take their highest rank.
  const RANK = { super_admin: 100, admin: 90, department_admin: 60, content_manager: 40, team_lead: 30, associate: 10 };
  const rankOf = (u) => {
    if (!u) return 0;
    const roles = Array.isArray(u.positions) && u.positions.length
      ? u.positions.map((p) => p.role).filter(Boolean)
      : [u.role];
    return Math.max(...roles.map((r) => RANK[r] || 0), 0);
  };
  const canModifyNode = (targetUser) => {
    if (!user || !targetUser) return false;
    if (user.role === "super_admin" || user.role === "admin") return true;
    if (user.id === targetUser.id) return false; // never remove yourself from hierarchy
    return rankOf(user) > rankOf(targetUser);
  };
  const isDAOfDept = (deptKey) => user?.role === "department_admin" && (user?.departments || []).includes(deptKey);
  const canAddCM = (deptKey) => user?.role === "super_admin" || user?.role === "admin" || isDAOfDept(deptKey);
  const canAddTL = (deptKey) => user?.role === "super_admin" || user?.role === "admin" || user?.role === "content_manager" || isDAOfDept(deptKey);
  const canAddProject = (deptKey) => user?.role === "super_admin" || user?.role === "admin" || user?.role === "content_manager" || user?.role === "team_lead" || isDAOfDept(deptKey);

  // Iter 36.8 — Strict scope-link RBAC for CM / TL rows. Rules per user's
  // directive: only DA-of-dept / admin / super_admin / the CM (or TL)
  // themselves / that TL's CM can drill into a CM or TL scope dashboard.
  // Everyone else sees the row as plain text (still expandable). The
  // department-level dashboard is intentionally NOT gated — its data is
  // RBAC-scoped by the backend, so every user sees only their own slice.
  const canOpenCMScope = (cm) => user && (
    user.role === "super_admin" || user.role === "admin" ||
    isDAOfDept(cm?.department || "content") ||
    user.id === cm?.id
  );
  const canOpenTLScope = (tl) => user && (
    user.role === "super_admin" || user.role === "admin" ||
    isDAOfDept(tl?.department || "content") ||
    user.id === tl?.id ||
    // CM of this TL
    (user.role === "content_manager" && tl?.manager_id === user.id)
  );

  // Group SEO folders by parent_id for tree rendering
  const seoByParent = useMemo(() => {
    const m = { null: [] };
    for (const f of (seoFolders || [])) {
      const p = f.parent_id || null;
      (m[p] = m[p] || []).push(f);
    }
    return m;
  }, [seoFolders]);
  const canManageSeo = user?.role === "super_admin" || user?.role === "admin" || (user?.role === "department_admin" && (user?.departments || []).includes("seo"));
  const renderSeoFolderTree = (parentId, depth) => {
    const kids = seoByParent[parentId] || [];
    return kids.map((f) => {
      const grandkids = seoByParent[f.id] || [];
      const hasKids = grandkids.length > 0;
      const isOpen = expanded[`seof-${f.id}`] !== false;
      return (
        <div key={f.id}>
          <div className="flex items-stretch gap-1" style={{ paddingLeft: depth * 10 }}>
            {hasKids ? (
              <button onClick={() => toggle(`seof-${f.id}`)} className="text-slate-400 hover:text-slate-700 px-0.5" data-testid={`seof-toggle-${f.key}`}>
                {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              </button>
            ) : <span className="w-4" />}
            <NavLink
              to={`/seo/folder/${f.id}`}
              data-testid={`seo-folder-link-${f.key}`}
              onContextMenu={(e) => openContext(e, "seo_folder", f.id, f.name)}
              className={({isActive}) => `flex items-center gap-2 flex-1 min-w-0 pl-2 pr-2 py-1 rounded-md text-sm transition-colors ${isActive ? "bg-purple-50 text-purple-700 font-medium" : "text-slate-600 hover:bg-slate-50"}`}
            >
              <FolderOpen className="h-3.5 w-3.5 text-purple-500" />
              <span className="flex-1 truncate">{f.name}</span>
              {f.type === "keyword_planning" && <span className="text-[9px] px-1 rounded bg-fuchsia-100 text-fuchsia-700 uppercase tracking-wider">KP</span>}
              {f.system && <span className="text-[9px] uppercase tracking-wider text-slate-400">default</span>}
            </NavLink>
            {canQuickCreate && (
              <QuickCreateButton
                onCreate={openQuickCreate("seo_folder", f.id, f.name)}
                testId={`quick-add-seof-${f.key}`}
                extraItems={canManageSeo ? [{
                  label: "New Sub-folder",
                  testId: `seof-add-sub-${f.key}`,
                  icon: <FolderOpen className="h-4 w-4 mr-2 text-amber-500" />,
                  onClick: async () => {
                    const name = window.prompt(`New sub-folder inside "${f.name}":`);
                    if (!name) return;
                    try {
                      const r = await api.post("/seo/folders", { name, parent_id: f.id });
                      setSeoFolders((all) => [...all, r.data]);
                      setExpanded((e) => ({ ...e, [`seof-${f.id}`]: true }));
                      toast.success("Sub-folder created");
                    } catch (e) { toast.error(formatApiError(e)); }
                  },
                }] : null}
              />
            )}
          </div>
          {(listsByParent[`seo_folder:${f.id}`] || []).length > 0 && (
            <div className="ml-6 mt-0.5 space-y-0.5 border-l border-slate-100 pl-2" style={{ marginLeft: depth * 10 + 24 }}>
              {(listsByParent[`seo_folder:${f.id}`] || []).map((lst) => (
                <SidebarListRow key={lst.id} list={lst} onContextMenu={(e) => openContext(e, "sidebar_list", lst.id, lst.name)} onQuickCreate={canQuickCreate ? openQuickCreate("sidebar_list", lst.id, lst.name) : null} />
              ))}
            </div>
          )}
          {hasKids && isOpen && renderSeoFolderTree(f.id, depth + 1)}
        </div>
      );
    });
  };


  const submitRename = async () => {
    if (!renaming?.value?.trim()) { setRenaming(null); return; }
    const newName = renaming.value.trim();
    try {
      if (renaming.kind === "project") {
        await api.patch(`/projects/${renaming.id}`, { name: newName });
      } else if (renaming.kind === "team") {
        // Iter 36.7 Phase 2.6 — prefer the persisted team endpoint so the
        // rename fans out to every affiliated TL server-side and preserves
        // the team even if it has 0 TLs. Falls back to the legacy per-TL
        // PATCH for teams that don't yet have a row.
        if (renaming.team_row_id) {
          await api.patch(`/teams/${renaming.team_row_id}`, { name: newName });
        } else {
          const tlIds = String(renaming.id).split(",").filter(Boolean);
          await Promise.all(tlIds.map((tid) => api.patch(`/users/${tid}`, { team_name: newName })));
        }
      } else {
        await api.patch(`/users/${renaming.id}`, { name: newName });
      }
      toast.success("Renamed");
      setRenaming(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  // Resizable sidebar — persisted in localStorage. Min 240px, max 560px.
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const v = parseInt(localStorage.getItem("contenthub.sidebarW") || "320", 10);
    return isNaN(v) ? 320 : Math.min(560, Math.max(240, v));
  });
  const resizeRef = useRef(null);
  useEffect(() => {
    const onMove = (e) => {
      if (!resizeRef.current) return;
      const w = Math.min(560, Math.max(240, e.clientX));
      setSidebarWidth(w);
    };
    const onUp = () => { if (resizeRef.current) { localStorage.setItem("contenthub.sidebarW", String(sidebarWidth)); resizeRef.current = false; document.body.style.cursor = ""; document.body.style.userSelect = ""; } };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [sidebarWidth]);
  const startResize = () => { resizeRef.current = true; document.body.style.cursor = "col-resize"; document.body.style.userSelect = "none"; };

  // Iter 45.66 — collapsible sidebar. Persisted in localStorage so the
  // working area (right side) can go full-width across reloads. The width
  // animates to 0; a floating expand button appears on the top bar.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("contenthub.sidebarCollapsed") === "1"; } catch { return false; }
  });
  const toggleSidebarCollapse = () => setSidebarCollapsed((c) => {
    const next = !c;
    try { localStorage.setItem("contenthub.sidebarCollapsed", next ? "1" : "0"); } catch {}
    return next;
  });

  return (
    <div className="h-screen w-screen bg-slate-50 flex overflow-hidden">
      {/* Day Splash — full-screen welcome on the first login of the day.
          Rendered here so it overlays the entire authed shell. Auto-dismisses
          after 15 s; sets localStorage.daySplashLastShown to today. */}
      <DaySplash user={user} />
      <aside
        className={`shrink-0 bg-white border-r flex flex-col relative overflow-hidden ${sidebarCollapsed ? "border-transparent" : "border-slate-200"}`}
        data-testid="sidebar"
        data-collapsed={sidebarCollapsed}
        style={{ width: sidebarCollapsed ? 0 : sidebarWidth, transition: "width 200ms ease" }}
      >
        <div className="h-16 px-5 flex items-center gap-2.5 border-b border-slate-200 shrink-0" style={{ width: sidebarWidth }}>
          <div className="h-9 w-9 flex items-center justify-center">
            <img src="/logo/pluto-logo.png" alt="PLUTO" className="h-9 w-9 object-contain" data-testid="brand-icon" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-brand tracking-brand text-lg text-slate-900" data-testid="brand-logo">PLUTO</span>
            <span className="text-[9px] font-medium tracking-[0.18em] uppercase text-slate-400 mt-0.5">by CreateX</span>
          </div>
          <button
            onClick={toggleSidebarCollapse}
            className="ml-auto p-1.5 rounded-md text-slate-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
            data-testid="sidebar-collapse-btn"
            title="Collapse sidebar"
            aria-label="Collapse sidebar"
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-1">
          {/* Iter 42.5.4 — Mark Productivity CTA as a prominent left-sidebar
              button for producers (associates + team leads). Placed at the
              top of the nav (above My Tasks) with a soft purple gradient
              so it's the first thing a writer/editor/publisher sees when
              they need to log the day's output. */}
          {canMarkProductivity && (
            <button
              onClick={() => setProdMarkOpen(true)}
              data-testid="mark-productivity-btn"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 shadow-sm hover:shadow transition-all mb-1"
            >
              <Sparkles className="h-4 w-4" /> Mark My Productivity
            </button>
          )}
          <NavLink to="/dash" end data-testid="nav-dashboard"
            className={({isActive}) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive?"bg-purple-50 text-purple-700":"text-slate-700 hover:bg-slate-100"}`}>
            <LayoutDashboard className="h-4 w-4" /> Dashboard
          </NavLink>
          {(user?.role === "super_admin" || user?.role === "admin" || user?.role === "department_admin" || user?.role === "content_manager") && (
            <NavLink to="/seo-performance" data-testid="nav-seo-performance"
              className={({isActive}) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive?"bg-purple-50 text-purple-700":"text-slate-700 hover:bg-slate-100"}`}>
              <TrendingUp className="h-4 w-4" /> SEO Performance
            </NavLink>
          )}
          <NavLink to="/my-tasks" data-testid="nav-my-tasks"
            className={({isActive}) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive?"bg-purple-50 text-purple-700":"text-slate-700 hover:bg-slate-100"}`}>
            <Sparkles className="h-4 w-4" /> My Tasks
          </NavLink>
          <NavLink to="/projects" end data-testid="nav-projects"
            className={({isActive}) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive?"bg-purple-50 text-purple-700":"text-slate-700 hover:bg-slate-100"}`}>
            <FolderOpen className="h-4 w-4" /> Projects
          </NavLink>
          {/* Iter 36.9.13 — People tab is visible to every logged-in user
              except plain associates. Backend `/api/users` already RBAC-scopes
              the roster (`get_visible_user_ids`), so each role only sees their
              own pool. Fixes bug 11: Publishing TLs were previously hidden
              from the link when their primary role happened to be Dept Admin. */}
          {user && user.role !== "associate" && (
            <NavLink to="/team" data-testid="nav-team"
              className={({isActive}) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive?"bg-purple-50 text-purple-700":"text-slate-700 hover:bg-slate-100"}`}>
              <Users className="h-4 w-4" /> People
            </NavLink>
          )}
          {/* Productivity Report — visible to admin / super_admin / dept_admin /
              CM / TL. Backend scopes rows via get_visible_user_ids so each
              role only sees their own tree. */}
          {user && ["super_admin", "admin", "department_admin", "content_manager", "team_lead"].includes(user.role) && (
            <NavLink to="/productivity" data-testid="nav-productivity"
              className={({isActive}) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive?"bg-purple-50 text-purple-700":"text-slate-700 hover:bg-slate-100"}`}>
              <TrendingUp className="h-4 w-4" /> Productivity Report
            </NavLink>
          )}
          {/* Iter 45.2 — "My Attendance" moved into /profile per user ask.
              Sidebar entry removed; the /attendance route now redirects to
              /profile (see App.js) so old bookmarks / deep-links still land
              on the right place. Team Attendance below stays because it's a
              manager-scoped view of everyone's rows. */}
          {/* Iter 36.9.13 — Bug 19. Managers (TL / CM / DA / Admin / SA) get
              a Team-Attendance view too — same UI as the HR grid but the
              backend RBAC-scopes rows to their `get_visible_user_ids`. HR
              dept members already reach it via /hr/attendance. */}
          {(user?.role === "team_lead" || user?.role === "content_manager" || user?.role === "department_admin" || user?.role === "super_admin" || user?.role === "admin") && (
            <NavLink to="/team-attendance" data-testid="nav-team-attendance"
              className={({isActive}) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive?"bg-purple-50 text-purple-700":"text-slate-700 hover:bg-slate-100"}`}>
              <CalendarCheck className="h-4 w-4" /> Team Attendance
            </NavLink>
          )}
          {(user?.role === "super_admin" || user?.role === "admin" || user?.role === "content_manager" || user?.role === "team_lead"
            || (user?.departments || [user?.department] || []).map((d) => (d||"").toLowerCase()).includes("hr")) && (
            <NavLink to="/payroll" data-testid="nav-payroll"
              className={({isActive}) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive?"bg-purple-50 text-purple-700":"text-slate-700 hover:bg-slate-100"}`}>
              <Wallet className="h-4 w-4" /> Payroll
            </NavLink>
          )}

          <div className="pt-4 pb-1 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Departments
          </div>

          {tree.map((dept) => {
            const DeptIcon = dept.icon;
            const deptKey = `dept-${dept.key}`;
            const deptOpen = !!expanded[deptKey];
            // Iter 36.8.2 — Every user can click a Department to open its
            // "combined" dashboard (task spreadsheet + project list). Backend
            // /tasks and /projects both intersect the caller's `get_visible_
            // projects` scope, so non-admin users automatically see only the
            // projects assigned to them. No frontend gating needed — the
            // dashboard renders the SAME structure for everyone but with the
            // right slice of data. HR + SEO keep their bespoke URLs.
            const scopeUrl = dept.kind === "hr"
              ? "/hiring-requests"
              : dept.key === "seo"
                ? "/seo"
                : `/scope/department/${dept.key}`;

            // Hide HR dept entirely if the current user isn't allowed to see hiring requests.
            // Iter 36.8 — HR is now STRICTLY scoped to admin/super_admin OR
            // users whose positions include HR. Blanket CM/TL access is
            // removed (they were previously exposed to /hiring, /hr/attendance,
            // /payroll under HR — which leaked HR staff and payroll to every
            // TL/CM). CMs/TLs who legitimately need HR access must be given
            // an HR position on their profile.
            if (dept.kind === "hr") {
              const userDepts = new Set([
                ...(user?.departments || []),
                user?.department,
                ...((user?.positions || []).map((p) => p?.department)),
              ].filter(Boolean).map((d) => (d || "").toLowerCase()));
              const canSeeHR = user && (
                user.role === "super_admin" || user.role === "admin" || userDepts.has("hr")
              );
              if (!canSeeHR) return null;
            }

            return (
              <div key={dept.key}>
                <div className={`flex items-center gap-0.5 pr-1 ${dept.active ? "" : "opacity-60"}`} data-testid={`dept-row-${dept.key}`}>
                  <button
                    data-testid={`dept-toggle-${dept.key}`}
                    onClick={() => dept.active && toggle(deptKey)}
                    className="p-1 rounded hover:bg-slate-100 text-slate-400"
                    disabled={!dept.active}
                  >
                    {dept.active ? (deptOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />) : <span className="w-3.5 inline-block" />}
                  </button>
                  {dept.active ? (
                    <NavLink
                      to={scopeUrl}
                      data-testid={`dept-link-${dept.key}`}
                      onContextMenu={dept.kind === "hr" ? undefined : (e) => openContext(e, "department", dept.key, dept.label)}
                      className={({isActive}) => `flex items-center gap-2 flex-1 min-w-0 px-1.5 py-1.5 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-purple-50 text-purple-700" : "text-slate-800 hover:bg-slate-50"}`}
                    >
                      <DeptIcon className="h-4 w-4" />
                      <span className="flex-1 text-left truncate">{dept.label}</span>
                    </NavLink>
                  ) : (
                    <div className="flex items-center gap-2 flex-1 min-w-0 px-1.5 py-1.5 text-sm text-slate-400">
                      <DeptIcon className="h-4 w-4" />
                      <span className="flex-1 text-left truncate">{dept.label}</span>
                      <span className="text-[10px] uppercase text-slate-400">Soon</span>
                    </div>
                  )}
                  {dept.active && canQuickCreate && (
                    <QuickCreateButton
                      onCreate={openQuickCreate("department", dept.key, dept.label)}
                      testId={`quick-add-dept-${dept.key}`}
                      extraItems={dept.key === "seo" && (user?.role === "super_admin" || user?.role === "admin" || (user?.role === "department_admin" && (user?.departments || []).includes("seo"))) ? [{
                        label: "New Folder",
                        testId: "seo-add-folder-menu",
                        icon: <FolderOpen className="h-4 w-4 mr-2 text-amber-500" />,
                        onClick: async () => {
                          const name = window.prompt("New folder name:");
                          if (!name) return;
                          try { const r = await api.post("/seo/folders", { name }); setSeoFolders((all) => [...all, r.data]); toast.success("Folder created"); } catch (e) { toast.error(formatApiError(e)); }
                        },
                      }] : null}
                    />
                  )}
                </div>

                {/* HR dept — show only Hiring Requests sub-link, skip CM/TL/project tree */}
                {dept.kind === "hr" && dept.active && deptOpen && (
                  <div className="ml-5 mt-1 space-y-0.5 border-l border-slate-100 pl-2">
                    <NavLink
                      to="/hiring-requests"
                      data-testid="nav-hiring"
                      className={({isActive}) => `flex items-center gap-2 pl-2 pr-2 py-1 rounded-md text-sm transition-colors ${isActive ? "bg-rose-50 text-rose-700 font-medium" : "text-slate-600 hover:bg-slate-50"}`}
                    >
                      <ClipboardList className="h-3.5 w-3.5 text-rose-500" /> Hiring Requests
                    </NavLink>
                    <NavLink
                      to="/hr/attendance"
                      data-testid="nav-hr-attendance"
                      className={({isActive}) => `flex items-center gap-2 pl-2 pr-2 py-1 rounded-md text-sm transition-colors ${isActive ? "bg-rose-50 text-rose-700 font-medium" : "text-slate-600 hover:bg-slate-50"}`}
                    >
                      <CalendarCheck className="h-3.5 w-3.5 text-rose-500" /> Attendance
                    </NavLink>
                    <NavLink
                      to="/payroll"
                      data-testid="nav-hr-payroll"
                      className={({isActive}) => `flex items-center gap-2 pl-2 pr-2 py-1 rounded-md text-sm transition-colors ${isActive ? "bg-rose-50 text-rose-700 font-medium" : "text-slate-600 hover:bg-slate-50"} ${(user?.role === "admin" || user?.role === "super_admin") ? "hidden" : ""}`}
                    >
                      <Wallet className="h-3.5 w-3.5 text-rose-500" /> Payroll
                    </NavLink>
                    {(listsByParent[`department:${dept.key}`] || []).map((lst) => (
                      <SidebarListRow key={lst.id} list={lst} onContextMenu={(e) => openContext(e, "sidebar_list", lst.id, lst.name)} onQuickCreate={canQuickCreate ? openQuickCreate("sidebar_list", lst.id, lst.name) : null} />
                    ))}
                  </div>
                )}

                {dept.key === "seo" && dept.active && deptOpen && (
                  <div className="ml-5 mt-1 space-y-0.5 border-l border-slate-100 pl-2" data-testid="seo-tree">
                    {renderSeoFolderTree(null, 0)}
                    {(listsByParent[`department:${dept.key}`] || []).map((lst) => (
                      <SidebarListRow key={lst.id} list={lst} onContextMenu={(e) => openContext(e, "sidebar_list", lst.id, lst.name)} onQuickCreate={canQuickCreate ? openQuickCreate("sidebar_list", lst.id, lst.name) : null} />
                    ))}
                  </div>
                )}

                {dept.kind !== "hr" && dept.key !== "seo" && dept.active && deptOpen && (
                  <div className="ml-5 mt-1 space-y-0.5 border-l border-slate-100 pl-2">
                    {/* Marketing Teams — flat list of user-created teams. */}
                    {dept.key === "marketing" && marketingTeams.map((mt) => (
                      <div key={mt.id} className="group flex items-stretch gap-0.5 pr-1" data-testid={`mkt-team-sidebar-row-${mt.id}`}>
                        <span className="w-4" />
                        <NavLink
                          to={`/marketing/teams/${mt.id}`}
                          onContextMenu={(e) => openContext(e, "marketing_team", mt.id, mt.name)}
                          className={({isActive}) => `flex items-center gap-2 flex-1 min-w-0 pl-2 pr-2 py-1 rounded-md text-sm transition-colors ${isActive ? "bg-purple-50 text-purple-700 font-medium" : "text-slate-700 hover:bg-slate-50"}`}
                          data-testid={`mkt-team-link-${mt.id}`}
                        >
                          <Users className="h-3.5 w-3.5 text-purple-500" />
                          <span className="flex-1 truncate">{mt.name}</span>
                        </NavLink>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              className="opacity-0 group-hover:opacity-100 h-6 w-6 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition"
                              data-testid={`mkt-team-menu-${mt.id}`}
                              title="Options"
                            >
                              <MoreHorizontal className="h-3.5 w-3.5" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="end" className="w-48 p-1">
                            <button onClick={() => setRenameMktTeam(mt)} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 rounded flex items-center gap-2" data-testid={`mkt-team-rename-${mt.id}`}><Pencil className="h-3.5 w-3.5" /> Rename</button>
                            <button onClick={() => setMembersMktTeam(mt)} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 rounded flex items-center gap-2" data-testid={`mkt-team-members-${mt.id}`}><Users className="h-3.5 w-3.5" /> Members</button>
                            <button onClick={() => setShareDialog({ kind: "marketing_team", id: mt.id, name: mt.name })} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 rounded flex items-center gap-2" data-testid={`mkt-team-share-${mt.id}`}><Share2 className="h-3.5 w-3.5 text-purple-500" /> Share access…</button>
                            <div className="h-px bg-slate-100 my-1" />
                            <button onClick={() => setDeleteMktTeam(mt)} className="w-full text-left px-3 py-1.5 text-sm hover:bg-red-50 text-red-600 rounded flex items-center gap-2" data-testid={`mkt-team-delete-${mt.id}`}><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                          </PopoverContent>
                        </Popover>
                      </div>
                    ))}
                    {/* "Add Team" — only Admin / SA / Marketing DA. */}
                    {dept.key === "marketing" && (user?.role === "super_admin" || user?.role === "admin" || (user?.role === "department_admin" && (user?.departments || []).includes("marketing"))) && (
                      <button
                        onClick={() => setCreateMktTeam(true)}
                        className="w-full flex items-center gap-2 pl-2 pr-2 py-1 rounded-md text-[11px] text-slate-500 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                        data-testid="mkt-team-add-btn"
                      >
                        <Plus className="h-3 w-3" /> New Team
                      </button>
                    )}
                    {(listsByParent[`department:${dept.key}`] || []).map((lst) => (
                      <SidebarListRow key={lst.id} list={lst} onContextMenu={(e) => openContext(e, "sidebar_list", lst.id, lst.name)} onQuickCreate={canQuickCreate ? openQuickCreate("sidebar_list", lst.id, lst.name) : null} />
                    ))}
                    {dept.managers.map((cm) => {
                      const cmKey = `cm-${cm.id}`;
                      const cmOpen = !!expanded[cmKey];
                      const cmMenuItems = canEdit ? [
                        { label: "Share access…", icon: <Share2 className="h-3.5 w-3.5 text-purple-500" />, onClick: () => setShareDialog({ kind: "cm", id: cm.id, name: cm.name }), testId: `cm-menu-share-${cm.id}` },
                        { label: "Rename", icon: <Pencil className="h-3.5 w-3.5 text-slate-500" />, onClick: () => setRenaming({ kind: "cm", id: cm.id, value: cm.name }), testId: `cm-menu-rename-${cm.id}` },
                        ...(canModifyNode(cm) ? [{ label: "Remove from hierarchy", icon: <Trash2 className="h-3.5 w-3.5" />, onClick: () => doDelete("cm", cm.id, cm.name), danger: true, divider: true, testId: `cm-menu-remove-${cm.id}` }] : []),
                      ] : [];
                      return (
                        <div key={cm.id}>
                          <TreeRow
                            testId={`cm-row-${cm.id}`}
                            open={cmOpen}
                            onToggle={() => toggle(cmKey)}
                            icon={<UserCog className="h-3.5 w-3.5 text-purple-500" />}
                            label={cm.name}
                            count={cm.teamLeads.length}
                            to={canOpenCMScope(cm) ? `/scope/manager/${cm.id}` : null}
                            onContextMenu={(e) => openContext(e, "cm", cm.id, cm.name)}
                            isEditing={renaming?.kind === "cm" && renaming?.id === cm.id}
                            editValue={renaming?.value || ""}
                            onEditChange={(v) => setRenaming({ ...renaming, value: v })}
                            onEditSave={submitRename}
                            onEditCancel={() => setRenaming(null)}
                            onQuickCreate={canQuickCreate ? openQuickCreate("manager", cm.id, cm.name) : null}
                            menuItems={cmMenuItems}
                          />
                          {cmOpen && (
                            <div className="ml-5 mt-0.5 space-y-0.5 border-l border-slate-100 pl-2">
                              {(listsByParent[`manager:${cm.id}`] || []).map((lst) => (
                                <SidebarListRow key={lst.id} list={lst} onContextMenu={(e) => openContext(e, "sidebar_list", lst.id, lst.name)} onQuickCreate={canQuickCreate ? openQuickCreate("sidebar_list", lst.id, lst.name) : null} />
                              ))}
                              {/* Iter 36.7 Phase 2.8 — flattened tree.
                                  TLs sit directly under their CM (no team
                                  grouping). Each TL renders its own projects
                                  below it. */}
                              {cm.teamLeads.map((tl) => {
                                // Iter 36.7 Phase 2.11 — pseudo TL node for
                                // CM self-picked-as-TL uses a distinct React
                                // key so it doesn't collide with the CM's
                                // real node. Its expanded state key is
                                // separate too.
                                const rowKey = tl._selfPickedFromCM ? `cmtl-${tl.id}` : tl.id;
                                const tlKey = tl._selfPickedFromCM ? `cmtl-${tl.id}` : `tl-${tl.id}`;
                                const tlOpen = !!expanded[tlKey];
                                const tlMenuItems = canEdit ? (
                                  tl._selfPickedFromCM ? [
                                    { label: "Share access…", icon: <Share2 className="h-3.5 w-3.5 text-purple-500" />, onClick: () => setShareDialog({ kind: "tl", id: tl.id, name: tl.name }), testId: `tl-menu-share-${tl.id}` },
                                  ] : [
                                    { label: "Share access…", icon: <Share2 className="h-3.5 w-3.5 text-purple-500" />, onClick: () => setShareDialog({ kind: "tl", id: tl.id, name: tl.name }), testId: `tl-menu-share-${tl.id}` },
                                    { label: "Rename", icon: <Pencil className="h-3.5 w-3.5 text-slate-500" />, onClick: () => setRenaming({ kind: "tl", id: tl.id, value: tl.name }), testId: `tl-menu-rename-${tl.id}` },
                                    ...(canModifyNode(tl) ? [{ label: "Remove from hierarchy", icon: <Trash2 className="h-3.5 w-3.5" />, onClick: () => doDelete("tl", tl.id, tl.name), danger: true, divider: true, testId: `tl-menu-remove-${tl.id}` }] : []),
                                  ]
                                ) : [];
                                return (
                                  <div key={rowKey}>
                                    <TreeRow
                                      testId={`tl-row-${tl.id}`}
                                      open={tlOpen}
                                      onToggle={() => toggle(tlKey)}
                                      icon={<UserCheck className="h-3.5 w-3.5 text-blue-500" />}
                                      label={
                                        <span className="inline-flex items-center gap-1.5">
                                          <span className="truncate">{tl.name}</span>
                                          {tl.workflow_type === "ai" && (
                                            <span
                                              data-testid={`tl-ai-pill-${tl.id}`}
                                              className="inline-flex items-center text-[8px] font-bold tracking-wider px-1 py-px rounded border border-purple-200 bg-gradient-to-r from-purple-50 to-fuchsia-50 text-purple-700 uppercase"
                                            >
                                              AI
                                            </span>
                                          )}
                                        </span>
                                      }
                                      count={tl.projects.length}
                                      isEditing={renaming?.kind === "tl" && renaming?.id === tl.id}
                                      editValue={renaming?.value || ""}
                                      onEditChange={(v) => setRenaming({ ...renaming, value: v })}
                                      onEditSave={submitRename}
                                      onEditCancel={() => setRenaming(null)}
                                      to={canOpenTLScope({ ...tl, department: dept.key }) ? `/scope/tl/${tl.id}` : null}
                                      onContextMenu={(e) => openContext(e, "tl", tl.id, tl.name)}
                                      onQuickCreate={canQuickCreate ? openQuickCreate("tl", tl.id, tl.name) : null}
                                      allowPublishing={dept.key === "content"}
                                      menuItems={tlMenuItems}
                                    />
                                    {tlOpen && (
                                      <div className="ml-5 mt-0.5 space-y-0.5 border-l border-slate-100 pl-2">
                                        {(listsByParent[`tl:${tl.id}`] || []).map((lst) => (
                                          <SidebarListRow key={lst.id} list={lst} onContextMenu={(e) => openContext(e, "sidebar_list", lst.id, lst.name)} onQuickCreate={canQuickCreate ? openQuickCreate("sidebar_list", lst.id, lst.name) : null} />
                                        ))}
                                        {tl.projects.map((p) => (
                                          <div key={p.id}>
                                            <ProjectRow
                                              project={p}
                                              renaming={renaming}
                                              setRenaming={setRenaming}
                                              onSave={submitRename}
                                              canEdit={canEdit || user?.role === "team_lead"}
                                              onContextMenu={(e) => openContext(e, "project", p.id, p.name)}
                                              onQuickCreate={canQuickCreate ? openQuickCreate("project", p.id, p.name) : null}
                                              onShare={() => setShareDialog({ kind: "project", id: p.id, name: p.name })}
                                              onDelete={() => doDelete("project", p.id, p.name)}
                                            />
                                            {(listsByParent[`project:${p.id}`] || []).length > 0 && (
                                              <div className="ml-5 mt-0.5 space-y-0.5 border-l border-slate-100 pl-2">
                                                {listsByParent[`project:${p.id}`].map((lst) => (
                                                  <SidebarListRow key={lst.id} list={lst} onContextMenu={(e) => openContext(e, "sidebar_list", lst.id, lst.name)} onQuickCreate={canQuickCreate ? openQuickCreate("sidebar_list", lst.id, lst.name) : null} />
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                        {canAddProject(dept.key) && (
                                          <button
                                            data-testid={`add-project-${tl.id}`}
                                            onClick={() => setAddDialog({ kind: "project", parent: { ...tl, department: dept.key } })}
                                            className="w-full flex items-center gap-2 pl-2 pr-2 py-1 rounded-md text-[11px] text-slate-500 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                                          >
                                            <Plus className="h-3 w-3" /> Projects
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              {canAddTL(dept.key) && (
                                <button
                                  data-testid={`add-tl-${cm.id}`}
                                  onClick={() => setAddDialog({ kind: "tl", parent: cm })}
                                  className="w-full flex items-center gap-2 pl-2 pr-2 py-1 rounded-md text-xs text-slate-500 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                                >
                                  <Plus className="h-3 w-3" /> Add Team Lead
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* per-TL projects render inside each TL branch; department-level unassigned bucket removed. */}

                    {/* Iter 36.9.9 — "Add Content Manager" removed from
                        Marketing per user directive. Content + SEO keep it. */}
                    {canAddCM(dept.key) && (dept.key === "content" || dept.key === "seo") && (
                      <button
                        data-testid="add-cm-btn"
                        onClick={() => setAddDialog({ kind: "cm", parent: { department: dept.key } })}
                        className="w-full flex items-center gap-2 pl-2 pr-2 py-1.5 mt-1 rounded-md text-sm text-slate-500 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                      >
                        <Plus className="h-3.5 w-3.5" /> Content Manager
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Shared with you */}
          {mySharesData.length > 0 && (
            <div>
              <div className="pt-4 pb-1 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <Share2 className="h-3 w-3" /> Shared with you
              </div>
              {mySharesData.map((s) => {
                const scopeName = s.scope_name
                  || users.find((u) => u.id === s.scope_id)?.name
                  || projects.find((p) => p.id === s.scope_id)?.name
                  || "Unknown";
                const to = s.scope_kind === "tl" ? `/scope/tl/${s.scope_id}`
                  : s.scope_kind === "manager" ? `/scope/manager/${s.scope_id}`
                  : s.scope_kind === "project" ? `/projects/${s.scope_id}`
                  : s.scope_kind === "marketing_team" ? `/marketing/teams/${s.scope_id}`
                  : s.scope_kind === "seo_folder" ? `/seo/folder/${s.scope_id}`
                  : s.scope_kind === "sidebar_list" ? (s.scope_kind_label === "Publishing Schedule" ? `/publishing/${s.scope_id}` : `/lists/${s.scope_id}`)
                  : s.scope_kind === "department" && s.scope_id === "seo" ? `/seo`
                  : `/scope/department/${s.scope_id}`;
                const Icon = s.scope_kind === "tl" ? UserCheck
                  : s.scope_kind === "manager" ? UserCog
                  : s.scope_kind === "marketing_team" ? Users
                  : s.scope_kind === "sidebar_list" ? (s.scope_kind_label === "Task List" ? ListChecks : Table)
                  : FolderOpen;
                const badgeLabel = s.scope_kind === "tl" ? "TL"
                  : s.scope_kind === "marketing_team" ? "Team"
                  : s.scope_kind === "sidebar_list" ? (s.scope_kind_label === "Custom Table" ? "Table" : "List")
                  : s.scope_kind;
                return (
                  <NavLink
                    key={s.id}
                    to={to}
                    data-testid={`shared-scope-${s.id}`}
                    className={({isActive}) => `flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${isActive?"bg-purple-50 text-purple-700 font-medium":"text-slate-700 hover:bg-slate-50"}`}
                  >
                    <Icon className="h-3.5 w-3.5 text-purple-400" />
                    <span className="truncate flex-1">{scopeName}</span>
                    <span className="text-[9px] uppercase text-slate-400">{badgeLabel}</span>
                  </NavLink>
                );
              })}
            </div>
          )}
        </nav>

        <div className="border-t border-slate-200 p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-purple-100 text-purple-700 font-semibold text-sm">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
                {user?.name}
                {user?.role === "super_admin" && user?.show_super_admin_badge !== false && (
                  <span className="text-[9px] font-bold tracking-wide uppercase bg-gradient-to-r from-amber-400 to-yellow-500 text-white px-1.5 py-0.5 rounded shadow-sm" data-testid="sidebar-super-admin-badge">
                    Super
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 truncate" data-testid="sidebar-user-designation">
                {(() => {
                  // Show the person's actual designation (e.g. "Content Team
                  // Lead", "Publishing Team Lead", "Content Manager"). Falls
                  // back to a formatted role label when position is unset,
                  // and hides the Super badge for hidden-badge admins.
                  if (user?.role === "super_admin" && user?.show_super_admin_badge === false) return "Admin";
                  if (user?.position) return user.position;
                  const roleLabel = {
                    super_admin: "Super Admin",
                    admin: "Admin",
                    content_manager: "Content Manager",
                    team_lead: user?.tl_kind === "publishing" ? "Publishing Team Lead" : "Content Team Lead",
                    associate: "Associate",
                    department_admin: `${(user?.departments || [user?.department] || [])[0] || ""} Department Admin`.trim(),
                  }[user?.role];
                  return roleLabel || user?.role?.replace("_", " ") || "";
                })()}
              </div>
            </div>
            {/* Iter 42.5.3 — Mark My Productivity moved from the per-project
                spreadsheet header to the profile row. Dialog hosts its own
                project picker so users can log from anywhere in the app.
                Iter 42.5.4 — moved out of this row and up into the sidebar
                as a prominent CTA (see the "producer nav" block above).
                Kept only Change Password + Log Out here. */}
            <button
              onClick={() => setPwOpen(true)}
              data-testid="change-password-btn"
              className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-purple-600 transition-colors"
              title="Change password"
            >
              <KeyRound className="h-4 w-4" />
            </button>
            <button onClick={async()=>{await logout(); nav("/login");}} data-testid="logout-btn" className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 transition-colors" title="Log out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
        <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
        <ProductivityMarkDialog
          open={prodMarkOpen}
          onClose={() => setProdMarkOpen(false)}
          currentUserId={user?.id}
        />
        {/* Resize handle — drag horizontally to widen the sidebar */}
        {!sidebarCollapsed && (
          <div
            onMouseDown={startResize}
            className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-purple-200/60 active:bg-purple-300 transition-colors"
            data-testid="sidebar-resizer"
            title="Drag to resize sidebar"
          />
        )}
      </aside>

      <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* Iter 44.6 — Global top-right "See Profile" chip. Sits outside the
            scroll container so it stays pinned across every page. Existing
            page-level sticky headers still stick to the top of the scroll
            area right below this bar. */}
        <div className="h-12 border-b border-slate-200 bg-white/90 backdrop-blur-md flex items-center justify-end px-6 gap-3 shrink-0 z-20">
          {/* Expand-sidebar button — only visible while the sidebar is collapsed. */}
          {sidebarCollapsed && (
            <button
              onClick={toggleSidebarCollapse}
              className="mr-auto flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-purple-600 hover:border-purple-300 hover:bg-purple-50 shadow-sm transition-all"
              data-testid="sidebar-expand-btn"
              title="Expand sidebar"
              aria-label="Expand sidebar"
            >
              <PanelLeftOpen className="h-4 w-4" />
              <span className="text-xs font-medium">Menu</span>
            </button>
          )}
          {/* Iter 45.50 — Global dark-mode toggle. Sits left of the profile pill. */}
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-btn"
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            className="relative h-9 w-9 flex items-center justify-center rounded-full border border-slate-200 bg-white hover:border-purple-300 hover:bg-purple-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all shadow-sm"
          >
            <Sun
              className="theme-toggle-icon absolute h-4 w-4 text-amber-500"
              data-visible={!isDark}
            />
            <Moon
              className="theme-toggle-icon absolute h-4 w-4 text-indigo-400"
              data-visible={isDark}
            />
          </button>

          {/* Iter 45.50 — Enhanced "My Profile" button: pill with gradient
              purple border, soft lifted shadow, chevron slides on hover. */}
          <button
            onClick={() => nav("/profile")}
            className="group relative flex items-center gap-2.5 pl-1 pr-4 py-1 rounded-full border border-purple-200 bg-gradient-to-r from-white via-purple-50/40 to-white hover:border-purple-400 hover:from-purple-50 hover:to-fuchsia-50 hover:shadow-[0_4px_14px_-4px_rgba(139,92,246,0.45)] hover:-translate-y-0.5 active:translate-y-0 transition-all shadow-sm"
            data-testid="topbar-see-profile"
            title="See your profile"
          >
            <Avatar className="h-8 w-8 ring-2 ring-white group-hover:ring-purple-200 transition-shadow">
              <AvatarFallback className="bg-gradient-to-br from-purple-500 to-violet-600 text-white font-semibold text-sm">{initials}</AvatarFallback>
            </Avatar>
            <div className="leading-tight text-left">
              <div className="text-[13px] font-semibold text-slate-800 group-hover:text-purple-700 transition-colors">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-purple-500 group-hover:text-purple-700 flex items-center gap-0.5 transition-colors">
                My Profile
                <ChevronRight className="h-2.5 w-2.5 transition-transform duration-200 group-hover:translate-x-0.5" />
              </div>
            </div>
          </button>
        </div>
        {/* Iter 42.3 — allow horizontal scroll on wide pages (Team,
            Payroll, Attendance, Productivity, SEO Performance, Projects
            etc.). Was previously `overflow-x-hidden` which capped every
            page and hid content on narrow viewports. */}
        <div className="flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-auto">
          <Outlet />
        </div>
      </main>

      <AddNodeDialog
        dialog={addDialog && addDialog.kind !== "project" ? addDialog : null}
        users={users}
        onClose={() => setAddDialog(null)}
        onDone={(hint = {}) => {
          setAddDialog(null);
          if (Array.isArray(hint.expandKeys) && hint.expandKeys.length) {
            setExpanded((prev) => {
              const next = { ...prev };
              hint.expandKeys.forEach((k) => { next[k] = true; });
              return next;
            });
          }
          reload();
        }}
      />

      {/* Iter 36.9.11 — sidebar's "Add project under TL X" now opens the
          full EditProjectDialog (same one the Projects tab uses) with the
          TL and the TL's CM prefilled, so the caller sees every field
          available on a project and can complete it in one step. */}
      <EditProjectDialog
        open={!!(addDialog && addDialog.kind === "project")}
        onClose={() => setAddDialog(null)}
        users={users}
        currentUser={user}
        mode="create"
        defaults={addDialog && addDialog.kind === "project" ? {
          team_lead_ids: [addDialog.parent.id],
          content_manager_ids: addDialog.parent.manager_id ? [addDialog.parent.manager_id] : [],
          department: addDialog.parent.department || "content",
        } : undefined}
        onSaved={() => { setAddDialog(null); reload(); }}
      />

      <QuickCreateDialog
        state={quickCreate}
        onClose={() => setQuickCreate(null)}
        onCreated={(created) => {
          setSidebarLists((all) => [...all, created]);
          setQuickCreate(null);
          nav(created.kind === "publishing_schedule" ? `/publishing/${created.id}` : `/lists/${created.id}`);
        }}
      />

      {/* Marketing Team dialogs — scoped lifetime so state resets on close. */}
      <CreateMarketingTeamDialog
        open={createMktTeam}
        onClose={() => setCreateMktTeam(false)}
        onCreated={(team) => {
          setMarketingTeams((all) => [...all, team]);
          setExpanded((e) => ({ ...e, "dept-marketing": true }));
          nav(`/marketing/teams/${team.id}`);
        }}
      />
      <RenameMarketingTeamDialog
        open={!!renameMktTeam}
        team={renameMktTeam}
        onClose={() => setRenameMktTeam(null)}
        onUpdated={(t) => {
          setMarketingTeams((all) => all.map((x) => (x.id === t.id ? t : x)));
          setRenameMktTeam(null);
        }}
      />
      <ManageMarketingMembersDialog
        open={!!membersMktTeam}
        team={membersMktTeam}
        users={users}
        onClose={() => setMembersMktTeam(null)}
        onUpdated={(t) => {
          setMarketingTeams((all) => all.map((x) => (x.id === t.id ? t : x)));
          setMembersMktTeam(null);
        }}
      />
      <DeleteMarketingTeamDialog
        open={!!deleteMktTeam}
        team={deleteMktTeam}
        onClose={() => setDeleteMktTeam(null)}
        onDeleted={(t) => {
          setMarketingTeams((all) => all.filter((x) => x.id !== t.id));
          setDeleteMktTeam(null);
          if (loc.pathname === `/marketing/teams/${t.id}`) nav("/scope/department/marketing");
        }}
      />

      {/* Context menu */}
      {contextMenu && (
        <div
          className="fixed z-50 bg-white rounded-lg shadow-2xl border border-slate-200 py-1 min-w-[180px]"
          style={{ top: Math.min(contextMenu.y, window.innerHeight - 160), left: Math.min(contextMenu.x, window.innerWidth - 220) }}
          onClick={(e) => e.stopPropagation()}
          data-testid="tree-context-menu"
        >
          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 truncate max-w-[220px]">
            {contextMenu.name}
          </div>
          {(contextMenu.kind === "cm" || contextMenu.kind === "tl" || contextMenu.kind === "team" || contextMenu.kind === "project" || contextMenu.kind === "department" || contextMenu.kind === "seo_folder" || contextMenu.kind === "sidebar_list") && (
            <button
              onClick={() => {
                // A "team" is virtual — share access lives on the underlying
                // Content TL scope (fallback to Publishing TL) so the grantee
                // sees every task under both TLs' projects.
                if (contextMenu.kind === "team") {
                  const tlId = (contextMenu.tls || [])[0];
                  if (!tlId) { toast.error("This team has no team lead yet — share not available"); setContextMenu(null); return; }
                  setShareDialog({ kind: "tl", id: tlId, name: contextMenu.name });
                } else {
                  setShareDialog({ kind: contextMenu.kind, id: contextMenu.id, name: contextMenu.name });
                }
                setContextMenu(null);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
              data-testid="ctx-share"
            >
              <Share2 className="h-3.5 w-3.5 text-purple-500" /> Share access…
            </button>
          )}
          {contextMenu.kind === "sidebar_list" && (
            <button
              onClick={() => { nav(`/lists/${contextMenu.id}`); setContextMenu(null); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
              data-testid="ctx-open"
            >
              <Pencil className="h-3.5 w-3.5 text-purple-500" /> Open & rename…
            </button>
          )}
          {/* Iter 36.9.8 — Embed Google Sheets URL on an SEO folder. Only
              shown for seo_folder rows; the folder must be one the caller
              can manage (backend enforces). */}
          {contextMenu.kind === "seo_folder" && (
            <button
              onClick={async () => {
                const url = window.prompt("Google Sheets URL to embed in this folder:", "");
                if (url === null) { setContextMenu(null); return; }
                try {
                  await api.patch(`/seo/folders/${contextMenu.id}`, { embed_url: url.trim() });
                  toast.success(url.trim() ? "Spreadsheet embedded" : "Embed cleared");
                  nav(`/seo/folder/${contextMenu.id}`);
                } catch (e) { toast.error(formatApiError(e)); }
                setContextMenu(null);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
              data-testid="ctx-embed-spreadsheet"
            >
              <ExternalLink className="h-3.5 w-3.5 text-purple-500" /> Embed Spreadsheet…
            </button>
          )}
          {contextMenu.kind !== "department" && (
            <button
              onClick={() => {
                if (contextMenu.kind === "marketing_team") {
                  const team = marketingTeams.find((t) => t.id === contextMenu.id);
                  if (team) setDeleteMktTeam(team);
                  setContextMenu(null);
                  return;
                }
                doDelete(contextMenu.kind, contextMenu.id, contextMenu.name, { tls: contextMenu.tls });
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
              data-testid="ctx-delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {(contextMenu.kind === "cm" || contextMenu.kind === "tl" || contextMenu.kind === "team")
                ? "Remove from hierarchy"
                : "Delete"}
            </button>
          )}
        </div>
      )}

      {/* Share dialog */}
      {shareDialog && (
        <ShareDialog
          scope={shareDialog}
          users={users}
          scopeKindMap={{ cm: "manager", tl: "tl", project: "project", department: "department", seo_folder: "seo_folder", sidebar_list: "sidebar_list", marketing_team: "marketing_team" }}
          onClose={() => setShareDialog(null)}
        />
      )}

      {/* Manage team dialog — full team-member management scoped to one team */}
      {manageTeam && (
        <ManageTeamDialog
          team={manageTeam.team}
          cm={manageTeam.cm}
          users={users}
          onClose={() => setManageTeam(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
}
